// Timestamps arrive in UTC (RFC 3339) and are rendered in the tenant's time
// zone (see useTimeFormat), falling back to the viewer's zone.

const relative = new Intl.RelativeTimeFormat('pt-BR', { numeric: 'auto' });

const steps: [Intl.RelativeTimeFormatUnit, number][] = [
  ['second', 60],
  ['minute', 60],
  ['hour', 24],
  ['day', 30],
  ['month', 12],
  ['year', Number.POSITIVE_INFINITY],
];

const cache = new Map<string, Intl.DateTimeFormat>();

function formatter(
  timeZone: string | undefined,
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  const key = `${timeZone ?? ''}|${JSON.stringify(options)}`;
  let f = cache.get(key);
  if (!f) {
    try {
      f = new Intl.DateTimeFormat('pt-BR', { ...options, ...(timeZone ? { timeZone } : {}) });
    } catch {
      // Unknown zone in this browser: fall back to the viewer's zone.
      f = new Intl.DateTimeFormat('pt-BR', options);
    }
    cache.set(key, f);
  }
  return f;
}

export function formatAbsolute(iso: string, timeZone?: string): string {
  return formatter(timeZone, { dateStyle: 'short', timeStyle: 'short' }).format(new Date(iso));
}

/** Date and time with seconds, for audit trails. */
export function formatPrecise(iso: string, timeZone?: string): string {
  return formatter(timeZone, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(new Date(iso));
}

export function formatClock(date: Date, timeZone?: string): string {
  return formatter(timeZone, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  }).format(date);
}

export function formatRelative(iso: string, now: Date = new Date()): string {
  let value = (new Date(iso).getTime() - now.getTime()) / 1000;
  for (const [unit, size] of steps) {
    if (Math.abs(value) < size) return relative.format(Math.round(value), unit);
    value /= size;
  }
  return formatAbsolute(iso);
}

/** IANA time zones offered in settings; UTC is not always listed by Intl. */
export function timeZones(): string[] {
  const zones = Intl.supportedValuesOf('timeZone');
  return zones.includes('UTC') ? zones : ['UTC', ...zones];
}
